import React, { useState, useCallback } from "react";
import { useHorseNaming } from "../hooks/useHorseNaming";
import { HorseNamingRequest, ApiProvider } from "../types/api";

const HorseNamingPanel: React.FC = () => {
    const { suggestions, loading, error, responseTime, usageStats, usageLoading, usageError, generateNames, refreshUsageStats } = useHorseNaming();
    const [formData, setFormData] = useState<HorseNamingRequest>({
        sireName: "",
        damName: "",
        gender: "Colt",
        limit: 4,
    });
    const [apiProvider, setApiProvider] = useState<ApiProvider>("OpenAI");
    const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
    const [activeSubTab, setActiveSubTab] = useState<"naming" | "stats">("stats");

    // Expandable rows state for usage statistics
    const [expandedStatsRows, setExpandedStatsRows] = useState<Set<string>>(new Set());

    // Toggle row expansion for usage statistics
    const toggleStatsRowExpansion = (rowId: string) => {
        const newExpanded = new Set(expandedStatsRows);
        if (newExpanded.has(rowId)) {
            newExpanded.delete(rowId);
        } else {
            newExpanded.add(rowId);
        }
        setExpandedStatsRows(newExpanded);
    };


    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Validate form
        if (!formData.sireName.trim() || !formData.damName.trim()) {
            setMessage({ type: "error", text: "Please enter both sire name and dam name" });
            setTimeout(() => setMessage(null), 5000);
            return;
        }

        if (formData.limit < 1) {
            setMessage({ type: "error", text: "Limit must be at least 1" });
            setTimeout(() => setMessage(null), 5000);
            return;
        }

        try {
            await generateNames(formData, apiProvider);
            setMessage(null);
        } catch (err) {
            // Error is already handled by the hook
        }
    };

    const handleChange = (field: keyof HorseNamingRequest, value: any) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleLimitChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = parseInt(e.target.value) || 0;
        handleChange("limit", value);
    };

    // Export Usage Statistics to CSV
    const exportUsageStatsToCSV = useCallback(() => {
        if (!usageStats || !usageStats.length) return;

        const formatCellValue = (value: any): string => {
            if (value === null || value === undefined) return "";
            if (typeof value === "object") return JSON.stringify(value);
            return String(value);
        };

        // Define headers for the CSV
        const headers = [
            "Source Name",
            "Description",
            "AI Provider",
            "Current Usage",
            "Total All Time",
            "Max Limit",
            "Last Used",
            "Monthly Usage Details"
        ];

        const csvContent = usageStats
            .map((stat) => {
                // Format monthly usage as a readable string
                const monthlyUsageStr = Object.keys(stat.monthlyUsage).length > 0
                    ? Object.entries(stat.monthlyUsage)
                        .sort(([a], [b]) => b.localeCompare(a))
                        .map(([month, usage]) => `${month}:${usage}`)
                        .join(";")
                    : "No monthly data";

                const row = [
                    stat.sourceName,
                    stat.description,
                    stat.aiProvider,
                    stat.usageCount,
                    stat.totalAllTime,
                    stat.maxLimit,
                    stat.lastUsed ? new Date(stat.lastUsed).toLocaleDateString() : "Never",
                    monthlyUsageStr
                ];

                return row
                    .map((value) => {
                        const stringValue = formatCellValue(value);
                        return stringValue.includes(",") || stringValue.includes(";")
                            ? `"${stringValue.replace(/"/g, '""')}"`
                            : stringValue;
                    })
                    .join(",");
            })
            .join("\n");

        const blob = new Blob([`${headers.join(",")}\n${csvContent}`], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `horse-naming-usage-stats-${new Date().toISOString().split("T")[0]}.csv`;
        link.click();
        URL.revokeObjectURL(url);
    }, [usageStats]);

    if (loading) {
        return (
            <div>
                <h2 className="panel-title">Horse Naming</h2>
                <div style={{ textAlign: "center", padding: "40px" }}>
                    <div style={{ fontSize: "18px", color: "#6c757d" }}>Generating horse names using {apiProvider} AI...</div>
                </div>
            </div>
        );
    }

    const renderNamingTab = () => (
        <div>
            {message && (
                <div
                    style={{
                        padding: "10px 15px",
                        marginBottom: "20px",
                        borderRadius: "4px",
                        backgroundColor: message.type === "success" ? "#d4edda" : "#f8d7da",
                        color: message.type === "success" ? "#155724" : "#721c24",
                        border: `1px solid ${message.type === "success" ? "#c3e6cb" : "#f5c6cb"}`,
                    }}
                >
                    {message.text}
                </div>
            )}

            {error && (
                <div
                    style={{
                        padding: "10px 15px",
                        marginBottom: "20px",
                        borderRadius: "4px",
                        backgroundColor: "#f8d7da",
                        color: "#721c24",
                        border: "1px solid #f5c6cb",
                    }}
                >
                    <strong>Error:</strong> {error}
                </div>
            )}

            <form onSubmit={handleSubmit}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: "20px", marginBottom: "20px" }}>
                    <div>
                        <label htmlFor="sireName" style={{ display: "block", marginBottom: "5px", fontWeight: "500" }}>
                            Sire Name *
                        </label>
                        <input
                            id="sireName"
                            type="text"
                            value={formData.sireName}
                            onChange={(e) => handleChange("sireName", e.target.value)}
                            style={{
                                // width: '100%',
                                padding: "8px 12px",
                                border: "1px solid #ced4da",
                                borderRadius: "4px",
                                fontSize: "14px",
                            }}
                            placeholder="Enter sire name"
                        />
                    </div>

                    <div>
                        <label htmlFor="damName" style={{ display: "block", marginBottom: "5px", fontWeight: "500" }}>
                            Dam Name *
                        </label>
                        <input
                            id="damName"
                            type="text"
                            value={formData.damName}
                            onChange={(e) => handleChange("damName", e.target.value)}
                            style={{
                                // width: '100%',
                                padding: "8px 12px",
                                border: "1px solid #ced4da",
                                borderRadius: "4px",
                                fontSize: "14px",
                            }}
                            placeholder="Enter dam name"
                        />
                    </div>

                    <div>
                        <label htmlFor="gender" style={{ display: "block", marginBottom: "5px", fontWeight: "500" }}>
                            Gender
                        </label>
                        <select
                            id="gender"
                            value={formData.gender}
                            onChange={(e) => handleChange("gender", e.target.value as HorseNamingRequest["gender"])}
                            style={{
                                // width: '100%',
                                padding: "8px 12px",
                                border: "1px solid #ced4da",
                                borderRadius: "4px",
                                fontSize: "14px",
                                backgroundColor: "white",
                            }}
                        >
                            <option value="Colt">Colt</option>
                            <option value="Filly">Filly</option>
                            <option value="Gelding">Gelding</option>
                            <option value="Mare">Mare</option>
                        </select>
                    </div>

                    <div>
                        <label htmlFor="limit" style={{ display: "block", marginBottom: "5px", fontWeight: "500" }}>
                            Number of Names
                        </label>
                        <input
                            id="limit"
                            type="number"
                            value={formData.limit}
                            onChange={handleLimitChange}
                            min="1"
                            style={{
                                // width: '100%',
                                padding: "8px 12px",
                                border: "1px solid #ced4da",
                                borderRadius: "4px",
                                fontSize: "14px",
                            }}
                            placeholder="4"
                        />
                    </div>
                </div>

                <div style={{ marginBottom: "20px" }}>
                    <label htmlFor="apiProvider" style={{ display: "block", marginBottom: "5px", fontWeight: "500" }}>
                        AI Provider
                    </label>
                    <select
                        id="apiProvider"
                        value={apiProvider}
                        onChange={(e) => setApiProvider(e.target.value as ApiProvider)}
                        style={{
                            width: "300px",
                            padding: "8px 12px",
                            border: "1px solid #ced4da",
                            borderRadius: "4px",
                            fontSize: "14px",
                            backgroundColor: "white",
                        }}
                    >
                        <option value="Anthropic">Anthropic</option>
                        <option value="OpenAI">OpenAI</option>
                        <option value="OpenRouter">OpenRouter</option>
                    </select>
                    <div style={{ fontSize: "12px", color: "#6c757d", marginTop: "5px" }}>Select your preferred AI provider for name generation</div>
                </div>

                <button
                    type="submit"
                    disabled={loading}
                    style={{
                        padding: "10px 20px",
                        backgroundColor: "#007bff",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: loading ? "not-allowed" : "pointer",
                        fontSize: "16px",
                        fontWeight: "500",
                        opacity: loading ? 0.6 : 1,
                    }}
                >
                    {loading ? "Generating..." : "Generate Names"}
                </button>
            </form>

            {suggestions && suggestions.length > 0 && (
                <div style={{ marginTop: "30px", textAlign: "left" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" }}>
                        <h3 style={{ color: "#2c3e50", margin: "0" }}>Generated Names</h3>
                        {responseTime && (
                            <div
                                style={{
                                    fontSize: "14px",
                                    color: "#6c757d",
                                    backgroundColor: "#e3f2fd",
                                    padding: "4px 8px",
                                    borderRadius: "4px",
                                    border: "1px solid #bbdefb",
                                }}
                            >
                                Response time: {responseTime}ms
                            </div>
                        )}
                    </div>

                    <div
                        style={{
                            backgroundColor: "#f8f9fa",
                            border: "1px solid #e9ecef",
                            borderRadius: "4px",
                            padding: "20px",
                        }}
                    >
                        <div style={{ display: "grid", gap: "15px" }}>
                            {suggestions.map((suggestion, index) => (
                                <div
                                    key={index}
                                    style={{
                                        padding: "15px",
                                        backgroundColor: "white",
                                        border: "1px solid #dee2e6",
                                        borderRadius: "4px",
                                        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                                        textAlign: "left",
                                    }}
                                >
                                    <div
                                        style={{
                                            fontSize: "16px",
                                            fontWeight: "600",
                                            color: "#2c3e50",
                                            marginBottom: "8px",
                                        }}
                                    >
                                        {suggestion.name}
                                    </div>
                                    <div
                                        style={{
                                            fontSize: "14px",
                                            color: "#6c757d",
                                            lineHeight: "1.5",
                                        }}
                                    >
                                        {suggestion.explanation || "No explanation provided"}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );

    const renderStatsTab = () => (
        <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
                <h3 style={{ color: "#2c3e50", margin: "0" }}>Usage Statistics</h3>
                <div style={{ display: "flex", gap: "10px" }}>
                    <button
                        onClick={refreshUsageStats}
                        disabled={usageLoading}
                        style={{
                            padding: "6px 12px",
                            backgroundColor: "#17a2b8",
                            color: "white",
                            border: "none",
                            borderRadius: "4px",
                            cursor: usageLoading ? "not-allowed" : "pointer",
                            fontSize: "14px",
                            opacity: usageLoading ? 0.6 : 1,
                        }}
                    >
                        {usageLoading ? "Refreshing..." : "Refresh Stats"}
                    </button>
                    {usageStats && usageStats.length > 0 && (
                        <button
                            onClick={exportUsageStatsToCSV}
                            style={{
                                padding: "6px 12px",
                                backgroundColor: "#28a745",
                                color: "white",
                                border: "none",
                                borderRadius: "4px",
                                cursor: "pointer",
                                fontSize: "14px",
                            }}
                        >
                            Export CSV
                        </button>
                    )}
                </div>
            </div>

            {usageError && (
                <div
                    style={{
                        padding: "10px 15px",
                        marginBottom: "20px",
                        backgroundColor: "#f8d7da",
                        color: "#721c24",
                        border: "1px solid #f5c6cb",
                        borderRadius: "4px",
                    }}
                >
                    <strong>Error:</strong> {usageError}
                </div>
            )}

            {usageStats && usageStats.length > 0 ? (
                <div
                    style={{
                        backgroundColor: "#f8f9fa",
                        border: "1px solid #e9ecef",
                        borderRadius: "4px",
                        overflow: "hidden",
                    }}
                >
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "40px 2fr 3fr 1fr 1fr 1fr 1fr 1.5fr",
                            gap: "0",
                            backgroundColor: "#e9ecef",
                            padding: "12px",
                            fontWeight: "600",
                            fontSize: "14px",
                            borderBottom: "1px solid #dee2e6",
                        }}
                    >
                        <div></div>
                        <div>Source Name</div>
                        <div>Description</div>
                        <div>Provider</div>
                        <div>Current</div>
                        <div>Total</div>
                        <div>Max Limit</div>
                        <div>Last Used</div>
                    </div>
                    {usageStats.map((stat, index) => {
                        const rowId = `usage-stat-${stat.id}`;
                        const isExpanded = expandedStatsRows.has(rowId);
                        const hasMonthlyData = Object.keys(stat.monthlyUsage).length > 0;

                        return (
                            <React.Fragment key={stat.id}>
                                <div
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns: "40px 2fr 3fr 1fr 1fr 1fr 1fr 1.5fr",
                                        gap: "0",
                                        padding: "12px",
                                        fontSize: "14px",
                                        backgroundColor: index % 2 === 0 ? "white" : "#f8f9fa",
                                        borderBottom: isExpanded ? "none" : (index < usageStats.length - 1 ? "1px solid #e9ecef" : "none"),
                                    }}
                                >
                                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                                        <button
                                            onClick={() => toggleStatsRowExpansion(rowId)}
                                            style={{
                                                background: "transparent",
                                                border: "none",
                                                cursor: hasMonthlyData ? "pointer" : "not-allowed",
                                                fontSize: "12px",
                                                padding: "2px",
                                                opacity: hasMonthlyData ? 1 : 0.3,
                                            }}
                                            title={hasMonthlyData ? (isExpanded ? "Collapse monthly details" : "Expand monthly details") : "No monthly data available"}
                                            disabled={!hasMonthlyData}
                                        >
                                            {hasMonthlyData ? (isExpanded ? "▼" : "▶") : "—"}
                                        </button>
                                    </div>
                                    <div style={{ fontWeight: "500" }}>{stat.sourceName}</div>
                                    <div style={{ color: "#6c757d" }}>{stat.description}</div>
                                    <div>
                                        <span
                                            style={{
                                                padding: "2px 6px",
                                                backgroundColor: stat.aiProvider === "anthropic" ? "#e3f2fd" : stat.aiProvider === "openai" ? "#e8f5e8" : "#fff3e0",
                                                color: stat.aiProvider === "anthropic" ? "#1565c0" : stat.aiProvider === "openai" ? "#2e7d32" : "#ef6c00",
                                                borderRadius: "3px",
                                                fontSize: "12px",
                                                textTransform: "capitalize",
                                            }}
                                        >
                                            {stat.aiProvider}
                                        </span>
                                    </div>
                                    <div style={{ fontWeight: "500" }}>{stat.usageCount}</div>
                                    <div>{stat.totalAllTime}</div>
                                    <div>{stat.maxLimit}</div>
                                    <div style={{ fontSize: "12px", color: "#6c757d" }}>{stat.lastUsed ? new Date(stat.lastUsed).toLocaleDateString() : "Never"}</div>
                                </div>

                                {/* Expanded row content */}
                                {isExpanded && hasMonthlyData && (
                                    <div
                                        style={{
                                            gridColumn: "1 / -1",
                                            backgroundColor: "#f8f9fa",
                                            padding: "15px",
                                            borderBottom: index < usageStats.length - 1 ? "1px solid #e9ecef" : "none",
                                        }}
                                    >
                                        <h4 style={{ margin: "0 0 15px 0", color: "#495057", fontSize: "14px" }}>
                                            📊 Monthly Usage Details for {stat.sourceName}
                                        </h4>
                                        <div
                                            style={{
                                                display: "grid",
                                                gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
                                                gap: "12px",
                                            }}
                                        >
                                            {Object.entries(stat.monthlyUsage)
                                                .sort(([a], [b]) => b.localeCompare(a)) // Sort by month descending (newest first)
                                                .map(([month, usage]) => {
                                                    const monthDate = new Date(month + "-01");
                                                    const monthName = monthDate.toLocaleDateString("en-US", { year: "numeric", month: "long" });

                                                    return (
                                                        <div
                                                            key={month}
                                                            style={{
                                                                backgroundColor: "white",
                                                                padding: "10px 12px",
                                                                borderRadius: "4px",
                                                                border: "1px solid #dee2e6",
                                                                fontSize: "13px",
                                                            }}
                                                        >
                                                            <div style={{ fontWeight: "600", color: "#2c3e50", marginBottom: "4px" }}>
                                                                {monthName}
                                                            </div>
                                                            <div style={{ color: "#6c757d" }}>
                                                                <strong>{usage}</strong> {usage === 1 ? "request" : "requests"}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                        </div>

                                        {/* Summary stats */}
                                        <div style={{ marginTop: "15px", padding: "10px", backgroundColor: "#e3f2fd", borderRadius: "4px", fontSize: "13px" }}>
                                            <strong>Summary:</strong> Total of {Object.values(stat.monthlyUsage).reduce((sum, count) => sum + count, 0)} requests across {Object.keys(stat.monthlyUsage).length} months
                                        </div>
                                    </div>
                                )}
                            </React.Fragment>
                        );
                    })}
                </div>
            ) : usageLoading ? (
                <div style={{ textAlign: "center", padding: "40px", color: "#6c757d" }}>Loading usage statistics...</div>
            ) : (
                <div style={{ textAlign: "center", padding: "40px", color: "#6c757d" }}>No usage statistics available</div>
            )}
        </div>
    );

    return (
        <div>
            {/* <h2 className="panel-title">Horse Naming</h2> */}

            {/* Sub-tab Navigation */}
            <nav
                style={{
                    display: "flex",
                    gap: "4px",
                    marginBottom: "30px",
                    borderBottom: "2px solid #e9ecef",
                }}
            >
                <button
                    className={`nav-button ${activeSubTab === "stats" ? "active" : ""}`}
                    onClick={() => setActiveSubTab("stats")}
                    style={{
                        padding: "8px 16px",
                        background: "transparent",
                        border: "none",
                        color: activeSubTab === "stats" ? "#007bff" : "#6c757d",
                        fontSize: "14px",
                        fontWeight: "500",
                        cursor: "pointer",
                        borderBottom: activeSubTab === "stats" ? "3px solid #007bff" : "3px solid transparent",
                        transition: "all 0.3s ease",
                        whiteSpace: "nowrap",
                    }}
                    onMouseEnter={(e) => {
                        if (activeSubTab !== "stats") {
                            e.currentTarget.style.color = "#495057";
                            e.currentTarget.style.backgroundColor = "#f8f9fa";
                        }
                    }}
                    onMouseLeave={(e) => {
                        if (activeSubTab !== "stats") {
                            e.currentTarget.style.color = "#6c757d";
                            e.currentTarget.style.backgroundColor = "transparent";
                        }
                    }}
                >
                    Usage Statistics
                </button>
                <button
                    className={`nav-button ${activeSubTab === "naming" ? "active" : ""}`}
                    onClick={() => setActiveSubTab("naming")}
                    style={{
                        padding: "8px 16px",
                        background: "transparent",
                        border: "none",
                        color: activeSubTab === "naming" ? "#007bff" : "#6c757d",
                        fontSize: "14px",
                        fontWeight: "500",
                        cursor: "pointer",
                        borderBottom: activeSubTab === "naming" ? "3px solid #007bff" : "3px solid transparent",
                        transition: "all 0.3s ease",
                        whiteSpace: "nowrap",
                    }}
                    onMouseEnter={(e) => {
                        if (activeSubTab !== "naming") {
                            e.currentTarget.style.color = "#495057";
                            e.currentTarget.style.backgroundColor = "#f8f9fa";
                        }
                    }}
                    onMouseLeave={(e) => {
                        if (activeSubTab !== "naming") {
                            e.currentTarget.style.color = "#6c757d";
                            e.currentTarget.style.backgroundColor = "transparent";
                        }
                    }}
                >
                    Horse Naming
                </button>
            </nav>

            {/* Tab Content */}
            {activeSubTab === "naming" ? renderNamingTab() : renderStatsTab()}
        </div>
    );
};

export default HorseNamingPanel;
